/**
 * Leash.ts — The elastic leash: spring physics + the drawn cord.
 *
 * This is where the player's input and the dog's willpower get combined into
 * actual motion. Every frame:
 *
 *   1. Read the player's desired (input) velocity and the dog's desired (AI) velocity.
 *   2. Model the leash as a damped spring (Hooke's law). If the gap between them
 *      exceeds the slack length, a restoring pull proportional to the stretch is
 *      added to BOTH — dragging the player toward the dog and reining the dog in.
 *   3. Hard-clamp the pair to the leash's maximum length, so a bolting dog can
 *      never run off the map — and so a walker can never wander away from a dog
 *      that is busy going. Which of the two gets moved depends on whether the
 *      dog is anchored; see `update()`.
 *   4. Write the final velocities and lay out the cord, which reddens and
 *      straightens out of its sag as the dog strains.
 *
 * ── What the cord's colour means ────────────────────────────────────────────
 * Not a measurement of the cord. `strain` is set from outside — it's the lunge
 * strength the HazardManager measures (systems/frenzy.ts), the same number
 * behind the barking and the frenzy meter. The lead used to draw its own
 * stretch, which meant it lit up brightest when the walker had simply walked
 * off without their dog. Reading the *dog's mood* instead is what makes the
 * lead an early warning rather than a restatement of the geometry you can
 * already see.
 *
 * ── The spring math (tune via config/constants.ts → LEASH) ──────────────────
 *   dx, dy   = dog - player                 // vector along the leash (ground plane)
 *   dist     = hypot(dx, dy)
 *   u        = (dx, dy) / dist              // unit vector, player → dog
 *   stretch  = max(0, dist - REST_LENGTH)   // how far past the slack we are
 *   pull     = min(STIFFNESS * stretch, MAX_PULL_SPEED)   // Hooke's F = k·x (as units/s)
 *   // damping bleeds off the pull as the two are already closing, so the spring
 *   // settles instead of snapping past — DAMPING scales that easing.
 *   player.v += u * pull * PLAYER_MASS_FACTOR  // player dragged toward the dog
 *   dog.v    -= u * pull * DOG_MASS_FACTOR     // dog reined in (it's the lighter one)
 *
 * ── Who wins ────────────────────────────────────────────────────────────────
 * The walker is always the stronger of the two, in two layers:
 *
 *   1. Mass. PLAYER_MASS_FACTOR < DOG_MASS_FACTOR, so the same spring pull moves
 *      the dog more than it moves the walker.
 *   2. A floor on agency. resistLeashPull() guarantees the walker still makes at
 *      least MIN_PLAYER_PROGRESS of their intended headway along the direction
 *      they're steering. The dog can shove them sideways all it likes and can
 *      slow a direction right down, but it can't stall or reverse them — heading
 *      into a pull is hard work, never a brick wall.
 *
 * Make STIFFNESS bigger for a harder, snappier pull; make REST_LENGTH longer to
 * give the player more slack before anything happens. The math itself lives in the
 * pure, unit-tested systems/leashPhysics.ts.
 */
import * as THREE from 'three';
import { LEASH } from '../config/constants';
import { lerpHex } from '../utils/color';
import { clayMaterial } from '../render/clay';
import { computeLeashPull, leashStretch, resistLeashPull } from './leashPhysics';
import { vec2 } from '../utils/vec2';
import type { Player } from '../entities/Player';
import type { Dog } from '../entities/Dog';

export class Leash {
  /** The cord: straight shafts with a ball at every joint, all under one group. */
  private cord = new THREE.Group();
  private shafts: THREE.Mesh[] = [];
  private joints: THREE.Mesh[] = [];
  private shaftGeometry: THREE.CylinderGeometry;
  private jointGeometry: THREE.SphereGeometry;
  private material: THREE.MeshStandardMaterial;

  // Reusable scratch vectors so the per-frame math allocates nothing.
  private playerVel = vec2();
  private dogVel = vec2();
  private from = new THREE.Vector3();
  private to = new THREE.Vector3();
  private control = new THREE.Vector3();
  private a = new THREE.Vector3();
  private b = new THREE.Vector3();
  private axis = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);

  /**
   * How hard the dog is straining, 0..1 — written by the game core each frame
   * from the critter pull. Drives only how the cord is drawn (see `draw`); the
   * spring above is unaffected by it.
   */
  strain = 0;

  constructor(
    scene: THREE.Scene,
    private player: Player,
    private dog: Dog,
  ) {
    const r = LEASH.THICKNESS / 2;
    // A shaft is a unit-tall cylinder scaled along its own axis each frame, so
    // the radius stays put however far the lead stretches. Balls at the joints
    // round the kinks off — shaft plus ball is the shape you get rolling clay
    // between your palms, which is the only way a cylinder belongs in this town
    // (see render/clay.ts).
    this.shaftGeometry = new THREE.CylinderGeometry(r, r, 1, 8);
    this.jointGeometry = new THREE.SphereGeometry(r, 8, 6);
    // `opts` deliberately asks clay.ts for an *uncached* material: the colour is
    // mutated every frame with the strain, and a shared one would drag every
    // object of that colour with it.
    this.material = clayMaterial(LEASH.SLACK_COLOR, {});

    for (let i = 0; i < LEASH.SEGMENTS; i++) {
      const shaft = new THREE.Mesh(this.shaftGeometry, this.material);
      shaft.castShadow = true;
      this.shafts.push(shaft);
      this.cord.add(shaft);
    }
    for (let i = 0; i <= LEASH.SEGMENTS; i++) {
      const joint = new THREE.Mesh(this.jointGeometry, this.material);
      joint.castShadow = true;
      this.joints.push(joint);
      this.cord.add(joint);
    }
    this.cord.frustumCulled = false; // every part moves every frame
    scene.add(this.cord);
  }

  update(): void {
    // 1. Desired velocities from each actor's own logic.
    this.player.getDesiredVelocity(this.playerVel);
    this.dog.getDesiredVelocity(this.dogVel);

    // A dog that is mid-squat, or standing over what it just left, is an
    // *anchor* rather than a passenger: the spring stops moving it and the hard
    // cap pulls the walker up short instead. Without this the lead quietly tows
    // a rooted dog along behind the walker, which is why the poop could end up
    // yards from the animal the cleanup meter measures against — and why
    // walking out of cleanup range was impossible, the dog simply followed.
    const anchored = this.dog.isBusy;

    // 2. Leash geometry (player → dog), on the ground plane.
    const dx = this.dog.pos.x - this.player.pos.x;
    const dy = this.dog.pos.y - this.player.pos.y;
    const dist = Math.hypot(dx, dy) || 0.0001; // guard divide-by-zero
    const ux = dx / dist;
    const uy = dy / dist;

    const stretch = leashStretch(dist, LEASH.REST_LENGTH);
    if (stretch > 0) {
      // Damping input: how fast the two are already closing along the leash axis
      // (>0 when the player is chasing the dog) — eases the pull off near contact.
      const closingSpeed =
        (this.playerVel.x - this.dogVel.x) * ux + (this.playerVel.y - this.dogVel.y) * uy;
      const pull = computeLeashPull(
        stretch,
        LEASH.STIFFNESS,
        LEASH.MAX_PULL_SPEED,
        LEASH.DAMPING,
        closingSpeed,
      );

      // Apply the spring pull: the dog is reined in toward the player, and the
      // player is dragged toward the dog by the smaller share they feel — then
      // braced so the dog can never stall or reverse their chosen direction.
      resistLeashPull(
        this.playerVel,
        ux * pull * LEASH.PLAYER_MASS_FACTOR,
        uy * pull * LEASH.PLAYER_MASS_FACTOR,
        LEASH.MIN_PLAYER_PROGRESS,
        this.playerVel,
      );
      if (!anchored) {
        this.dogVel.x -= ux * pull * LEASH.DOG_MASS_FACTOR;
        this.dogVel.y -= uy * pull * LEASH.DOG_MASS_FACTOR;
      }
    }

    // 3. Hard cap: the two are never farther apart than MAX_LENGTH. Normally
    //    that means reining the dog in; against an anchored dog it is the
    //    walker who reaches the end of the lead.
    if (dist > LEASH.MAX_LENGTH) {
      if (anchored) {
        this.player.setPosition(
          this.dog.pos.x - ux * LEASH.MAX_LENGTH,
          this.dog.pos.y - uy * LEASH.MAX_LENGTH,
        );
        // Kill the component still heading away, so it doesn't fight the clamp.
        const outward = -(this.playerVel.x * ux + this.playerVel.y * uy);
        if (outward > 0) {
          this.playerVel.x += ux * outward;
          this.playerVel.y += uy * outward;
        }
      } else {
        this.dog.setPosition(
          this.player.pos.x + ux * LEASH.MAX_LENGTH,
          this.player.pos.y + uy * LEASH.MAX_LENGTH,
        );
        // Kill any outward velocity component so it doesn't fight the clamp.
        const outward = this.dogVel.x * ux + this.dogVel.y * uy;
        if (outward > 0) {
          this.dogVel.x -= ux * outward;
          this.dogVel.y -= uy * outward;
        }
      }
    }

    // 4. Commit final velocities to the entities.
    this.player.vel.x = this.playerVel.x;
    this.player.vel.y = this.playerVel.y;
    this.dog.vel.x = this.dogVel.x;
    this.dog.vel.y = this.dogVel.y;
  }

  /**
   * Lay the cord between the walker's hand and the dog's collar, sagging in the
   * middle while the dog is relaxed and pulling straight and bright as it strains.
   *
   * The curve is a quadratic Bézier whose control point hangs below the midpoint.
   * A Bézier passes half way to its control point at t=0.5, so the control drops
   * by *twice* the sag we actually want.
   */
  draw(): void {
    this.from.set(this.player.pos.x, LEASH.HAND_HEIGHT, this.player.pos.y);
    this.to.set(this.dog.pos.x, LEASH.COLLAR_HEIGHT, this.dog.pos.y);
    this.control
      .addVectors(this.from, this.to)
      .multiplyScalar(0.5)
      .setY((LEASH.HAND_HEIGHT + LEASH.COLLAR_HEIGHT) / 2 - LEASH.SAG * (1 - this.strain) * 2);

    this.pointAt(0, this.a);
    this.joints[0].position.copy(this.a);
    for (let i = 0; i < LEASH.SEGMENTS; i++) {
      this.pointAt((i + 1) / LEASH.SEGMENTS, this.b);
      const shaft = this.shafts[i];
      shaft.position.addVectors(this.a, this.b).multiplyScalar(0.5);
      shaft.scale.set(1, this.a.distanceTo(this.b), 1);
      // Cylinders are modelled along +y, so aim this one down its own span.
      this.axis.subVectors(this.b, this.a).normalize();
      shaft.quaternion.setFromUnitVectors(this.up, this.axis);
      this.joints[i + 1].position.copy(this.b);
      this.a.copy(this.b);
    }

    this.material.color.setHex(lerpHex(LEASH.SLACK_COLOR, LEASH.TAUT_COLOR, this.strain));
  }

  /** Point at `t` along the sag curve, written into `out`. */
  private pointAt(t: number, out: THREE.Vector3): THREE.Vector3 {
    const u = 1 - t;
    return out
      .copy(this.from)
      .multiplyScalar(u * u)
      .addScaledVector(this.control, 2 * u * t)
      .addScaledVector(this.to, t * t);
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.cord);
    this.shaftGeometry.dispose();
    this.jointGeometry.dispose();
    this.material.dispose();
  }
}
